/* eslint-disable max-lines */
import { type CSSProperties, type ReactNode, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SnapshotSignal } from "./chat-home-signals"
import type { ContinueChat, GraphMeeting, HomeContextResponse } from "@/api/home-context"
import type { ExampleQuestion } from "@/types"
import type { ChatHomeSignals } from "@/types/user-settings"

import { MessageCircle, MoreHorizontal, MoveRight } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { useGeneralSettings } from "@/hooks/use-module-settings"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { SettingsService } from "@/services/settings-service"

import {
  buildSnapshotSignals,
  DEFAULT_CHAT_HOME_LAYOUT,
  DEFAULT_CHAT_HOME_SIGNALS,
  isChatHomeLayout,
} from "./chat-home-signals"
import { formatRelativeTime } from "./chat-home-utils"
import { DayBrief } from "./day-brief"
import { ExampleQuestions } from "./example-question"
import { Greeting, GREETING_TYPEWRITER_DURATION_MS } from "./greeting"
import { WeeklyHero } from "./weekly-hero"
import { HelloAILogo } from "../../../components/ui/hello-ai-logo"
import { createOneTimeAnimationStore, useOneTimeAnimation } from "../hooks/use-one-time-animation"

type ChatHomeProps = {
  exampleQuestions: ExampleQuestion[]
  isPersonalizationLoading?: boolean
  // True when the home_context query errored (after react-query's retry budget)
  // OR the response came back with every Graph signal flagged as failed and no
  // Hello AI content either — i.e. nothing at all could be shown. Without this,
  // ChatHome would silently fall back to the plain layout — indistinguishable
  // from "no personalization data yet" — and the user has no way to retry
  // without reloading the page.
  isPersonalizationError?: boolean
  // True when the home_context *query* itself errored, OR every single Graph
  // signal came back flagged as failed (a real Graph outage) even though the
  // request succeeded. Used to decide whether to warn on top of an already-
  // enriched (possibly stale-cached) layout: a single flaky leg there is
  // silent (the sections that loaded speak for themselves; Retry can't fix a
  // structural gap like a guest's missing mailbox), but losing EVERY Graph
  // signal at once means an active user could be looking at a calendar/inbox
  // that looks genuinely empty when it's actually just unavailable — e.g.
  // silently skipping meeting prep — so that case still needs the notice.
  isPersonalizationSystemicError?: boolean
  onExampleQuestionClick: (example: string) => void
  // Called when the user clicks Retry on the inline error notice. Wire this to
  // the home_context query's refetch().
  onRetryPersonalization?: () => void
  personalization?: HomeContextResponse
  onContinueChatClick?: (chatId: string, sessionType: string) => void
  onMemoryNudgeClick?: () => void
  onTrendingClick?: () => void
  onMeetingPrep?: (meeting: GraphMeeting) => void
  onWeeklyAction?: (mode: "wrap" | "plan") => void
}

const chatHomeAnimationStore = createOneTimeAnimationStore()
const CHAT_HOME_SIGNAL_PRIORITY: Array<keyof ChatHomeSignals> = [
  "meetings",
  "unreadMail",
  "projects",
  "people",
  "recentFiles",
  "trending",
  "memoryNudges",
]
const CHAT_HOME_LAYOUT_SIGNAL_CAPACITY = {
  "a1-strip": 5,
  "a2-rail": 5,
} satisfies Record<string, number>

/** Fade-in for example chips after greeting typewriter (aligned with `GREETING_TYPEWRITER_DURATION_MS`). */
const CHAT_HOME_EXAMPLES_ENTER_DELAY_MS = GREETING_TYPEWRITER_DURATION_MS

/** Shared with `<Greeting />` and `<HelloAILogo />` so typewriter timing matches. */
const CHAT_HOME_TYPEWRITER_START_DELAY_MS = 0

function hasPersonalizationContent(
  p: HomeContextResponse | undefined,
  canShowMemoryNudge: boolean,
  enabledSignals: ChatHomeSignals
): boolean {
  if (!p) return false
  const helloai = p.helloai
  const graph = p.graph
  const helloAiHasContent =
    helloai.continue_chats.length > 0 ||
    helloai.current_themes.length > 0 ||
    (enabledSignals.memoryNudges && canShowMemoryNudge && helloai.pending_memory_candidates > 0) ||
    (enabledSignals.projects && helloai.active_projects.length > 0)
  const graphHasContent =
    (enabledSignals.meetings &&
      (graph.next_meeting !== null || graph.upcoming_meetings.length > 0)) ||
    (enabledSignals.unreadMail && (graph.unread_mail?.count ?? 0) > 0) ||
    (enabledSignals.recentFiles && graph.recent_files.length > 0) ||
    (enabledSignals.trending && graph.trending.length > 0) ||
    (enabledSignals.people && graph.people.length > 0)
  // rendered_starters is theme-derived and may be empty even on success — don't gate
  // the enriched layout on it; only real Hello AI / Graph content counts as signal.
  return helloAiHasContent || graphHasContent
}

function getPromotedContinueChat(chat?: ContinueChat): ContinueChat | null {
  return chat && chat.message_count >= 3 ? chat : null
}

function ChatHomeLoadingShell() {
  return (
    <section className="space-y-8 pt-12" data-testid="chat-home-loading">
      <HelloAILogo className="mx-auto w-[60%]" />
      <Skeleton className="mx-auto h-7 w-96 max-w-[80%]" />
      <div className="border-primary/20 bg-primary/5 mx-auto max-w-2xl rounded-lg border px-5 py-4 shadow-sm">
        <div className="flex items-start gap-3">
          <Skeleton className="size-5" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      </div>
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-[72px] rounded-lg" />
        ))}
      </div>
    </section>
  )
}

function SnapshotTile({
  signal,
  compact = false,
  emphasis = "default",
  isMenuOpen = false,
  onMenuToggle,
}: {
  signal: SnapshotSignal
  compact?: boolean
  emphasis?: "default" | "quiet" | "strong"
  isMenuOpen?: boolean
  onMenuToggle?: (nextOpen: boolean) => void
}) {
  const isMobile = useIsMobile({ includeContentSidebarOpen: false })
  const Icon = signal.icon
  const content = (
    <>
      <div className="bg-muted/70 text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-md">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0">
        <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {signal.label}
        </p>
        <p className="text-foreground text-sm font-medium">{signal.value}</p>
        {!compact && signal.detail ? (
          <p className="text-muted-foreground line-clamp-1 text-xs">{signal.detail}</p>
        ) : null}
      </div>
    </>
  )
  let baseClassName =
    "border-border/50 bg-card text-card-foreground flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2 text-left shadow-sm"
  if (emphasis === "strong") {
    baseClassName =
      "border-primary/20 bg-primary/5 text-card-foreground flex min-w-0 items-center gap-3 rounded-lg border px-4 py-3 text-left shadow-sm"
  } else if (emphasis === "quiet") {
    baseClassName =
      "border-border/40 bg-background/70 text-card-foreground flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2 text-left"
  }

  if (!signal.actionLabel || (!signal.onClick && !signal.menuItems?.length)) {
    return (
      <div className={baseClassName} data-signal-id={signal.id}>
        {content}
      </div>
    )
  }

  const triggerClassName = `${baseClassName} hover:border-foreground/20 hover:bg-muted/40 focus-visible:ring-ring data-[state=open]:border-foreground/30 data-[state=open]:bg-muted/60 cursor-pointer transition-colors focus-visible:ring-2 focus-visible:outline-none`
  const items = signal.menuItems ?? []
  const hasMenu = items.length > 0

  if (!hasMenu) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={signal.actionLabel}
            className={triggerClassName}
            data-signal-id={signal.id}
            type="button"
            onClick={signal.onClick}
          >
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px]" side="top" sideOffset={6}>
          <p>{signal.actionLabel}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={isMenuOpen} onOpenChange={onMenuToggle}>
        <SheetTrigger asChild>
          <button
            aria-label={signal.actionLabel}
            className={triggerClassName}
            data-signal-id={signal.id}
            type="button"
          >
            {content}
          </button>
        </SheetTrigger>
        <SheetContent
          className="max-h-[85vh] overflow-y-auto rounded-t-xl"
          data-testid={`home-signal-menu-${signal.id}`}
          side="bottom"
        >
          <SheetHeader>
            <SheetTitle>{signal.label}</SheetTitle>
            <SheetDescription>{signal.value}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1">
            {items.map((item) => (
              <button
                key={item.id}
                className="hover:bg-muted focus-visible:ring-ring w-full rounded-md px-3 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                data-signal-menu-item-id={item.id}
                type="button"
                onClick={() => {
                  item.onSelect()
                  onMenuToggle?.(false)
                }}
              >
                <span className="text-foreground block text-sm font-medium">{item.label}</span>
                {item.description ? (
                  <span className="text-muted-foreground line-clamp-1 text-xs">
                    {item.description}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={onMenuToggle}>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={signal.actionLabel}
          className={triggerClassName}
          data-signal-id={signal.id}
          type="button"
        >
          {content}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-80 p-3"
        data-testid={`home-signal-menu-${signal.id}`}
      >
        <div className="space-y-0.5 px-1 pb-2">
          <p className="text-foreground text-sm font-medium">{signal.label}</p>
          <p className="text-muted-foreground text-xs">{signal.value}</p>
        </div>
        <div className="space-y-0.5">
          {items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className="flex cursor-pointer flex-col items-start gap-0.5 px-2 py-2"
              data-signal-menu-item-id={item.id}
              onSelect={() => item.onSelect()}
            >
              <span className="text-foreground text-sm font-medium">{item.label}</span>
              {item.description ? (
                <span className="text-muted-foreground line-clamp-1 text-xs">
                  {item.description}
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SnapshotStrip({
  columns = "standard",
  signals,
  testId = "home-snapshot-strip",
}: {
  columns?: "standard" | "five"
  signals: SnapshotSignal[]
  testId?: string
}) {
  const [openSignalId, setOpenSignalId] = useState<string | null>(null)
  if (signals.length === 0) return null
  const gridClassName =
    columns === "five"
      ? "grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
      : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
  return (
    <section className={gridClassName} data-testid={testId}>
      {signals.map((signal) => (
        <SnapshotTile
          key={signal.id}
          compact
          isMenuOpen={openSignalId === signal.id}
          signal={signal}
          onMenuToggle={(next) => setOpenSignalId(next ? signal.id : null)}
        />
      ))}
    </section>
  )
}

function SnapshotRail({
  signals,
  testId = "home-snapshot-rail",
}: {
  signals: SnapshotSignal[]
  testId?: string
}) {
  const [openSignalId, setOpenSignalId] = useState<string | null>(null)
  if (signals.length === 0) return null
  return (
    <aside className="grid min-h-[270px] content-between gap-2" data-testid={testId}>
      {signals.map((signal) => (
        <SnapshotTile
          key={signal.id}
          emphasis="quiet"
          isMenuOpen={openSignalId === signal.id}
          signal={signal}
          onMenuToggle={(next) => setOpenSignalId(next ? signal.id : null)}
        />
      ))}
    </aside>
  )
}

function ContinuePanel({
  chat,
  onContinueChatClick,
}: {
  chat: ContinueChat
  onContinueChatClick?: (chatId: string, sessionType: string) => void
}) {
  const { t, i18n } = useTranslation()

  const updatedLabel = formatRelativeTime(chat.updated_iso, i18n.language || "en")

  return (
    <section
      className="border-border/50 bg-card text-card-foreground rounded-lg border p-5 text-left shadow-sm"
      data-testid="home-continue-panel"
    >
      <div className="flex items-start gap-3">
        <div className="bg-muted/70 text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
          <MessageCircle className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("chatHome.continueWhereLeftOff", "Continue where you left off")}
          </p>
          <h3 className="text-foreground mt-2 line-clamp-2 text-lg font-medium">{chat.title}</h3>
          <p className="text-muted-foreground mt-1 text-xs">
            {t("chatHome.continueCards.messageCount", { count: chat.message_count })}{" "}
            {updatedLabel ? `· ${updatedLabel}` : ""}
          </p>
        </div>
      </div>
      <Button
        className="mt-4"
        type="button"
        onClick={() => onContinueChatClick?.(chat.chat_id, chat.session_type)}
      >
        {t("chatHome.continue", "Continue")}
        <MoveRight className="size-4" />
      </Button>
    </section>
  )
}

function splitSnapshotSignals(
  signals: SnapshotSignal[],
  layout: keyof typeof CHAT_HOME_LAYOUT_SIGNAL_CAPACITY
) {
  const sorted = [...signals].sort((a, b) => {
    const aIndex = CHAT_HOME_SIGNAL_PRIORITY.indexOf(a.id as keyof ChatHomeSignals)
    const bIndex = CHAT_HOME_SIGNAL_PRIORITY.indexOf(b.id as keyof ChatHomeSignals)
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex)
  })
  const capacity = CHAT_HOME_LAYOUT_SIGNAL_CAPACITY[layout]
  if (sorted.length <= capacity) {
    return { visible: sorted, overflow: [] }
  }

  const visibleCount = Math.max(1, capacity - 1)
  return {
    visible: sorted.slice(0, visibleCount),
    overflow: sorted.slice(visibleCount),
  }
}

function buildMoreSignal(
  overflow: SnapshotSignal[],
  t: ReturnType<typeof useTranslation>["t"]
): SnapshotSignal | null {
  if (overflow.length === 0) return null
  return {
    id: "more",
    label: t("chatHome.snapshot.moreLabel", "More"),
    value: t("chatHome.snapshot.moreValue", {
      count: overflow.length,
      defaultValue: `${overflow.length} more`,
    }),
    icon: MoreHorizontal,
    actionLabel: t("chatHome.snapshot.actions.moreSignals", "Show more signals"),
    menuItems: overflow.map((signal) => ({
      id: `more:${signal.id}`,
      label: signal.label,
      description: [signal.value, signal.detail].filter(Boolean).join(" · "),
      onSelect: () => {
        if (signal.menuItems?.[0]) {
          signal.menuItems[0].onSelect()
          return
        }
        signal.onClick?.()
      },
    })),
  }
}

function PersonalizationErrorNotice({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      className="border-destructive/30 bg-destructive/5 mx-auto flex w-full max-w-2xl items-center justify-between gap-3 rounded-lg border px-4 py-2.5"
      data-testid="personalization-error-notice"
      role="alert"
    >
      <p className="text-foreground/80 text-sm">
        {t("chatHome.personalizationError.message", {
          defaultValue: "Couldn't load your personal brief.",
        })}
      </p>
      {onRetry ? (
        <Button
          data-testid="personalization-error-retry"
          size="sm"
          variant="ghost"
          onClick={onRetry}
        >
          {t("chatHome.personalizationError.retry", { defaultValue: "Retry" })}
        </Button>
      ) : null}
    </div>
  )
}

export function ChatHome({
  exampleQuestions,
  isPersonalizationLoading = false,
  isPersonalizationError = false,
  isPersonalizationSystemicError = false,
  onExampleQuestionClick,
  onRetryPersonalization,
  personalization: receivedPersonalization,
  onContinueChatClick,
  onMemoryNudgeClick,
  onTrendingClick,
  onMeetingPrep,
  onWeeklyAction,
}: ChatHomeProps) {
  const prismMemory = usePrismMemory()
  const personalization =
    prismMemory && receivedPersonalization
      ? {
          ...receivedPersonalization,
          rendered_starters: [],
          helloai: {
            ...receivedPersonalization.helloai,
            current_themes: [],
            pending_memory_candidates: 0,
            frequent_sources: [],
          },
        }
      : receivedPersonalization
  const { t } = useTranslation()
  const settingsService = SettingsService.getInstance()
  const userName = settingsService.claims.name
  const { settings: generalSettings } = useGeneralSettings()

  const shouldAnimate = useOneTimeAnimation(chatHomeAnimationStore)
  const layout = isChatHomeLayout(generalSettings?.chatHomeLayout)
    ? generalSettings.chatHomeLayout
    : DEFAULT_CHAT_HOME_LAYOUT
  const enabledSignals: ChatHomeSignals = {
    ...DEFAULT_CHAT_HOME_SIGNALS,
    ...(generalSettings?.chatHomeSignals ?? {}),
  }

  const examplesEnterStyle: CSSProperties | undefined = shouldAnimate
    ? ({
        "--chat-home-examples-enter-delay": `${CHAT_HOME_EXAMPLES_ENTER_DELAY_MS}ms`,
      } as CSSProperties)
    : undefined

  const canShowMemoryNudge = Boolean(onMemoryNudgeClick)
  const isEnriched = hasPersonalizationContent(personalization, canShowMemoryNudge, enabledSignals)

  if (isPersonalizationLoading && !personalization) {
    return <ChatHomeLoadingShell />
  }

  if (!isEnriched || !personalization) {
    // Backward-compatible plain layout (translator/dash/explorer-empty).
    // When the home_context fetch errored, surface a small inline notice
    // above the plain content so the user knows personalization failed and
    // can retry — without this, the plain fallback is indistinguishable
    // from "no personalization data".
    return (
      <section className="space-y-8 pt-12" data-testid="chat-home">
        {isPersonalizationError ? (
          <PersonalizationErrorNotice onRetry={onRetryPersonalization} />
        ) : null}
        <HelloAILogo
          className="mx-auto w-[90%]"
          entranceAnimation={shouldAnimate}
          typewriterStartDelayMs={CHAT_HOME_TYPEWRITER_START_DELAY_MS}
        />
        <div>
          <Greeting
            typewriterStartDelayMs={CHAT_HOME_TYPEWRITER_START_DELAY_MS}
            userName={userName}
          />
        </div>
        <div className={shouldAnimate ? "chat-home-examples-enter" : ""} style={examplesEnterStyle}>
          <ExampleQuestions exampleQuestions={exampleQuestions} onClick={onExampleQuestionClick} />
        </div>
      </section>
    )
  }

  const themeLabel = personalization.helloai.current_themes[0]?.label
  const promotedContinueChat = getPromotedContinueChat(personalization.helloai.continue_chats[0])
  const greetingPersonalization = {
    nextMeeting: personalization.graph.next_meeting,
    continueChatTitle: promotedContinueChat ? promotedContinueChat.title : null,
    themeLabel: themeLabel,
    topCollaboratorName: personalization.graph.people[0]?.name ?? null,
  }

  const heroState = personalization.hero_state
  const showWeeklyHero = heroState === "weekly_wrap" || heroState === "weekly_plan"
  const weeklyMode: "wrap" | "plan" = heroState === "weekly_wrap" ? "wrap" : "plan"
  const snapshotSignals = buildSnapshotSignals({
    personalization,
    enabledSignals,
    t,
    onTrendingClick,
    onMemoryNudgeClick,
    onExampleQuestionClick,
    onMeetingPrep,
  })
  const { overflow, visible } = splitSnapshotSignals(snapshotSignals, layout)
  const moreSignal = buildMoreSignal(overflow, t)
  const visibleSignals = moreSignal ? [...visible, moreSignal] : visible
  const continuePanel = promotedContinueChat ? (
    <ContinuePanel chat={promotedContinueChat} onContinueChatClick={onContinueChatClick} />
  ) : null
  const meetingHero = (
    <DayBrief
      // a2-rail constrains the main column to share row with the snapshot rail —
      // use the compact treatment there. a1-strip is full-width so we get the
      // 2-col grid sections.
      compact={layout === "a2-rail"}
      enabledSignals={enabledSignals}
      personalization={personalization}
      onMeetingPrep={onMeetingPrep}
      onPromptClick={onExampleQuestionClick}
    />
  )
  const weeklyHero = showWeeklyHero ? (
    <WeeklyHero mode={weeklyMode} onAction={onWeeklyAction} />
  ) : null
  const heroPanel = (
    <>
      {meetingHero}
      {weeklyHero}
    </>
  )

  // Suggestion cards are intentionally NOT rendered in the enriched layouts.
  // Theme-derived starters now ride in the chat input as a passive typewriter
  // (see `useRotatingPlaceholder` in ai-explorer-page). The legacy fallback
  // above keeps `<ExampleQuestions>` for surfaces that don't fetch
  // home_context (translator/dash).
  let layoutContent: ReactNode
  if (layout === "a1-strip") {
    layoutContent = (
      <section className="space-y-6" data-testid="home-layout-centered-focus">
        {continuePanel}
        <SnapshotStrip columns="five" signals={visibleSignals} testId="home-visible-signals" />
      </section>
    )
  } else {
    layoutContent = (
      <section
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(240px,300px)] lg:items-start"
        data-testid="home-layout-context-rail"
      >
        <div className="min-w-0 space-y-6" data-testid="home-context-main">
          {heroPanel}
          {continuePanel}
        </div>
        <div className="lg:sticky lg:top-20">
          <SnapshotRail signals={visibleSignals} testId="home-visible-signals" />
        </div>
      </section>
    )
  }

  return (
    <section
      className="space-y-8 pt-12"
      data-chat-home-layout={layout}
      data-hero-state={heroState}
      data-testid="chat-home"
    >
      {/* Stale-data case: react-query's cached `data` is still rendered while
          a refetch is in flight. If that refetch errored, the user is looking
          at stale personalization data and needs to know — surface the same
          inline notice so they can retry. A single flaky Graph leg alone does
          NOT warrant this notice: the sections that loaded already show
          what's available, and for a structural gap (e.g. a guest account
          with no mailbox) Retry wouldn't fix anything anyway. But losing
          every Graph signal at once (isPersonalizationSystemicError) is a
          real outage worth flagging even on an otherwise-enriched layout. */}
      {isPersonalizationSystemicError ? (
        <PersonalizationErrorNotice onRetry={onRetryPersonalization} />
      ) : null}
      <HelloAILogo
        className="mx-auto w-[60%]"
        entranceAnimation={shouldAnimate}
        typewriterStartDelayMs={CHAT_HOME_TYPEWRITER_START_DELAY_MS}
      />
      <div>
        <Greeting
          personalization={greetingPersonalization}
          themeLabel={themeLabel}
          typewriterStartDelayMs={CHAT_HOME_TYPEWRITER_START_DELAY_MS}
          userName={userName}
        />
      </div>
      {layout === "a2-rail" ? null : heroPanel}
      {layoutContent}
      {overflow.length > 0 ? (
        <div className="sr-only" data-testid="home-overflow-signals">
          {overflow.map((signal) => signal.id).join(",")}
        </div>
      ) : null}
    </section>
  )
}
